import {rememberReturn,pendingReturn} from '../navigation/nativeReturn';
import {Capacitor,registerPlugin} from '@capacitor/core';
import {shouldExpireNativeSession} from './nativeSessionPolicy';
import {acceptIncoming} from '../navigation/nativeReturn';
import {dismissNativeKeyboard} from './nativeFiles';
import {closeTopLayer} from '../navigation/nativeBack';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';

const sessionLock=registerPlugin<{status():Promise<{enabled:boolean}>}>('FinFlowLock');

export class NativeFeatures {
  // Haptic feedback for better mobile experience
  static async lightHaptic() {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      }
  }

  static async mediumHaptic() {
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (error) {
      }
  }

  static async heavyHaptic() {
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (error) {
      }
  }

  static async successHaptic() {
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch (error) {
      }
  }

  static async errorHaptic() {
    try {
      await Haptics.notification({ type: NotificationType.Error });
    } catch (error) {
      }
  }

  // Status bar management
  static async setStatusBarStyle(style: 'light' | 'dark' = 'dark') {
    try {
      await StatusBar.setStyle({ style: style === 'light' ? Style.Light : Style.Dark });
    } catch (error) {
      }
  }

  static async setStatusBarBackgroundColor(color: string = '#F7F8F5') {
    try {
      await StatusBar.setBackgroundColor({ color });
    } catch (error) {
      }
  }

  // App state management
  static async getAppState() {
    try {
      const state = await App.getState();
      return state;
    } catch (error) {
      return { isActive: true };
    }
  }

  // Initialize native features
  private static initialized=false;
  static async initialize() {
    if(this.initialized||!Capacitor.isNativePlatform())return;
    this.initialized=true;
    try {
      // Set up status bar
      const appearance=()=>{const dark=document.documentElement.classList.contains('dark');void this.setStatusBarStyle(dark?'light':'dark');void this.setStatusBarBackgroundColor(dark?'#101c1c':'#F7F8F5');};
      appearance();new MutationObserver(appearance).observe(document.documentElement,{attributes:true,attributeFilter:['class']});

      // iOS sessions expire after ten minutes without app interaction. The protected
      // Keychain enrollment remains available for a new, server-verified sign-in.
      if(Capacitor.getPlatform()==='ios'){
        let lastInteraction=Date.now(),expired=false;
        const touch=()=>{lastInteraction=Date.now();expired=false;};
        for(const name of ['pointerdown','keydown','touchstart'])window.addEventListener(name,touch,{passive:true});
        window.setInterval(()=>{
          if(!expired&&Date.now()-lastInteraction>=10*60*1000){expired=true;if(!pendingReturn())rememberReturn(window.location.pathname+window.location.search);window.dispatchEvent(new Event('finflow-auto-logout'));}
        },1000);
      }
      // Set up app state listeners
      App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) window.dispatchEvent(new Event('finflow-app-resumed'));
        try {
          const now = Date.now();
          const key = 'finflow_last_inactive';
          if (!isActive) {
            localStorage.setItem(key, String(now));
          } else {
            const last = Number(localStorage.getItem(key) || '0');
            const elapsedMs = now - (Number.isFinite(last) ? last : 0);
            // Native lock already covers the app; keep its authenticated session.
            if (last > 0 && await shouldExpireNativeSession(elapsedMs,()=>sessionLock.status(),Capacitor.getPlatform()==='ios')) {
              if(!pendingReturn())rememberReturn(window.location.pathname+window.location.search);
              const evt = new CustomEvent('finflow-auto-logout');
              window.dispatchEvent(evt);
              // Clear marker to avoid repeated firings
            }
            localStorage.removeItem(key);
          }
        } catch {
          // no-op
        }
        });

      await App.addListener('appUrlOpen', data => acceptIncoming(data.url));
      try{const launch=await App.getLaunchUrl();if(launch?.url)acceptIncoming(launch.url);}catch{/* Warm link listener remains active. */}
      if(Capacitor.getPlatform()==='android')await App.addListener('backButton',async()=>{
        try{if(await dismissNativeKeyboard())return;}catch{return;}
        if(closeTopLayer())return;
        window.dispatchEvent(new Event('finflow-native-back'));
      });

      App.addListener('appRestoredResult', () => {
        // Restored native results are not payment, connection or export proof.
        window.dispatchEvent(new Event('finflow-app-resumed'));
      });

      } catch (error) {
      }
  }
}

// Mobile-specific utilities
export const MobileUtils = {
  // Check if running on iOS
  isIOS: () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  },

  // Check if running on mobile
  isMobile: () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  },

  // Get safe area insets
  getSafeAreaInsets: () => {
    const style = getComputedStyle(document.documentElement);
    return {
      top: parseInt(style.getPropertyValue('--sat') || '0'),
      bottom: parseInt(style.getPropertyValue('--sab') || '0'),
      left: parseInt(style.getPropertyValue('--sal') || '0'),
      right: parseInt(style.getPropertyValue('--sar') || '0')
    };
  },

  // Add haptic feedback to button clicks
  addHapticToButton: (element: HTMLElement) => {
    element.addEventListener('click', () => {
      NativeFeatures.lightHaptic();
    });
  },

  // Prevent zoom on input focus (iOS)
  preventZoomOnFocus: () => {
    const inputs = document.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      input.addEventListener('focus', () => {
        // Set viewport to prevent zoom
        const viewport = document.querySelector('meta[name=viewport]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
        }
      });

      input.addEventListener('blur', () => {
        // Restore viewport
        const viewport = document.querySelector('meta[name=viewport]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1');
        }
      });
    });
  }
};

export default NativeFeatures;
